import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Badge,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Textarea,
  Input,
  Label,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@tonic/shared/components/ui';
import {
  ArrowLeft,
  Calendar,
  Wrench,
  Package,
  DollarSign,
  History,
  Camera,
  FileText,
  MoreVertical,
  Send,
  Clock,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { mockRentals } from './mock-data';

// Find the rental item from the rentals that contains this product
function findRentalItemDetails(productId) {
  for (const rental of mockRentals) {
    const item = rental.items.find(item => item.productId === productId);
    if (item) {
      return {
        ...item,
        rental,
        maintenanceHistory: rental.maintenanceHistory?.filter(h => h.itemId === productId) || []
      };
    }
  }
  return null;
}

function getStatusColor(status) {
  switch (status.toLowerCase()) {
    case 'active':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'completed':
      return 'destructive';
    default:
      return 'outline';
  }
}

function getConditionColor(condition) {
  switch (condition?.toLowerCase()) {
    case 'excellent':
      return 'default';
    case 'good':
      return 'secondary';
    case 'fair':
      return 'outline';
    case 'poor':
      return 'destructive';
    default:
      return 'outline';
  }
}

export function RentalItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState('details');
  const [maintenanceNote, setMaintenanceNote] = React.useState('');

  // Find the rental item details
  const item = findRentalItemDetails(id);

  const handleBackClick = () => {
    navigate('/admin/re-commerce/rental/items');
  };

  const handleAddMaintenanceNote = () => {
    if (maintenanceNote.trim()) {
      console.log("Adding maintenance note:", maintenanceNote);
      setMaintenanceNote('');
    }
  };

  if (!item) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={handleBackClick}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Items
        </Button>
        <div className="mt-6 text-center">
          <h2 className="text-xl font-semibold">Item Not Found</h2>
          <p className="text-muted-foreground mt-2">The requested rental item could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBackClick}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Items
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{item.name}</h1>
            <p className="text-sm text-muted-foreground">SKU: {item.sku}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={getConditionColor(item.condition.current || item.condition.checkout)}>
            {item.condition.current || item.condition.checkout}
          </Badge>
          <Badge variant={getStatusColor(item.rental.status)}>
            {item.rental.status}
          </Badge>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>View Rental Details</DropdownMenuItem>
              <DropdownMenuItem>Schedule Maintenance</DropdownMenuItem>
              <DropdownMenuItem>Update Status</DropdownMenuItem>
              <DropdownMenuItem>Edit Details</DropdownMenuItem>
              <DropdownMenuItem>Print Details</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="rental">Rental Info</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Item Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Product ID</Label>
                  <p className="font-medium">{item.productId}</p>
                </div>
                <div>
                  <Label>SKU</Label>
                  <p className="font-medium">{item.sku}</p>
                </div>
                <div>
                  <Label>Current Condition</Label>
                  <Badge variant={getConditionColor(item.condition.current || item.condition.checkout)}>
                    {item.condition.current || item.condition.checkout}
                  </Badge>
                </div>
                <div>
                  <Label>Daily Rate</Label>
                  <p className="font-medium">${item.pricing.dailyRate}</p>
                </div>
                <div>
                  <Label>Deposit</Label>
                  <p className="font-medium">${item.pricing.deposit}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rental" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Current Rental Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rental ID</Label>
                  <p className="font-medium">{item.rental.id}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge variant={getStatusColor(item.rental.status)}>
                    {item.rental.status}
                  </Badge>
                </div>
                <div>
                  <Label>Customer</Label>
                  <p className="font-medium">{item.rental.customer.name}</p>
                  <p className="text-sm text-muted-foreground">{item.rental.customer.email}</p>
                </div>
                <div>
                  <Label>Rental Period</Label>
                  <p className="font-medium">
                    {new Date(item.rental.rentalPeriod.startDate).toLocaleDateString()} - {new Date(item.rental.rentalPeriod.endDate).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Add a maintenance note..."
                  value={maintenanceNote}
                  onChange={(e) => setMaintenanceNote(e.target.value)}
                />
                <Button onClick={handleAddMaintenanceNote}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                {item.maintenanceHistory.map((record, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <Wrench className="w-4 h-4 mt-1 text-neutral-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{record.type}</span>
                        <span className="text-sm text-muted-foreground">
                          by {record.technician} on {new Date(record.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-1">{record.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Rental History</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">No previous rental history available.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 
